import { motion } from "framer-motion";
import { ArrowRight, Ship } from "lucide-react";
import { COMPANY } from "@/config/company";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.12 },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.7 + i * 0.12 },
  }),
};

function MaskLine({ children, i }) {
  return (
    <span className="block overflow-hidden">
      <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-end overflow-hidden"
    >
      <motion.img
        src="https://images.pexels.com/photos/262353/pexels-photo-262353.jpeg"
        alt="Cargo ship at sunset representing global trade"
        className="absolute inset-0 h-full w-full object-cover"
        initial={{ scale: 1.15 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-[#0f2018]/95"></div>
      <div className="absolute inset-0 grain"></div>

      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 md:pb-32 pt-40">
        <div className="max-w-4xl">
          <motion.div
            variants={fade} custom={0} initial="hidden" animate="show"
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-[#C5A059]"></span>
            <p className="overline text-[#E6C97A]">{COMPANY.tagline}</p>
          </motion.div>

          <h1 className="font-serif-display text-white text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.98] tracking-tight">
            <MaskLine i={0}>Trusted roots.</MaskLine>
            <MaskLine i={1}>
              <span className="text-[#E6C97A] italic font-medium">Global reach.</span>
            </MaskLine>
          </h1>

          <motion.p
            variants={fade} custom={1} initial="hidden" animate="show"
            className="mt-8 text-white/85 text-lg md:text-xl max-w-2xl leading-relaxed"
          >
            Aadiyogis EXIM Traders sources, certifies and delivers premium agricultural
            commodities to buyers across five continents — grains, spices, oilseeds and
            fresh produce, moved with precision.
          </motion.p>

          <motion.div
            variants={fade} custom={2} initial="hidden" animate="show"
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a href="#products" data-testid="hero-cta-button" className="btn-gold group">
              Explore Products
              <ArrowRight size={18} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a href="#rfq" className="btn-ghost">Request a Quote</a>
          </motion.div>

          <motion.div
            variants={fade} custom={3} initial="hidden" animate="show"
            className="mt-16 grid grid-cols-3 gap-8 max-w-2xl"
          >
            {[
              { k: "40+", v: "Countries served" },
              { k: "180K MT", v: "Annual volume" },
              { k: "17 yrs", v: "Trade heritage" },
            ].map((s) => (
              <div key={s.v} className="border-t border-white/20 pt-4">
                <div className="font-serif-display text-white text-3xl md:text-4xl">{s.k}</div>
                <div className="text-white/70 text-xs md:text-sm tracking-wider uppercase mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-6 right-6 md:right-10 hidden md:flex items-center gap-2 text-white/70 text-xs tracking-widest uppercase">
        <Ship size={16} strokeWidth={1.25} />
        Kavali · Nellore · Hyderabad · India
      </div>
    </section>
  );
}
